import {definePerfDuration, definePerfSingleMark} from '../addPerfMark';

export const PANEL_BANK_SEARCH_MAX_PAIRING_DURATION_MS = 30_000;

const panelBankSearchMarks = {
  applied: 'panel_bank_search_applied',
  appliedInputStarted: 'panel_bank_search_applied_input_started',
  cleared: 'panel_bank_search_cleared',
  firstInputSearchApplied: 'panel_bank_search_first_input_search_applied',
  firstInputStarted: 'panel_bank_search_first_input_started',
  historyItemSelected: 'panel_bank_search_history_item_selected',
} as const;

/**
 * Discrete RUM action for a deliberate clear. Typing is intentionally excluded
 * from single actions so high-volume typing stays represented by duration
 * context instead of one action per value change.
 */
export const panelBankSearchClearMark = definePerfSingleMark({
  actionName: panelBankSearchMarks.cleared,
  markName: panelBankSearchMarks.cleared,
});

/**
 * Discrete RUM action for choosing a saved search. The duration vitals still
 * measure whether the resulting search apply was slow.
 */
export const panelBankSearchHistoryItemMark = definePerfSingleMark({
  actionName: panelBankSearchMarks.historyItemSelected,
  markName: panelBankSearchMarks.historyItemSelected,
});

/**
 * User pain signal: measures from the first detected search edit while the
 * debounced rendered query is behind, until the matching debounced search is
 * applied. This is a debounce/apply window, not proof that the user finished
 * typing; typingInputCount/supersededTypingInputCount show how many typing
 * changes arrived before the 100ms debounce/render path caught up.
 */
export const panelBankSearchFirstInputToSearchAppliedDuration =
  definePerfDuration({
    durationName: 'panel_bank_search_first_input_to_search_applied',
    startMark: panelBankSearchMarks.firstInputStarted,
    endMark: panelBankSearchMarks.firstInputSearchApplied,
    description:
      'panel search first input to matching PanelBankSections search applied',
    owner: 'workspace',
    maxPairingDurationMs: PANEL_BANK_SEARCH_MAX_PAIRING_DURATION_MS,
    devtools: {
      track: 'Panel search',
      trackGroup: 'Workspace',
      color: 'primary',
    },
  });

/**
 * User pain signal: measures from the latest detected value change that wins
 * the debounce/apply window to the matching search being applied. This is not
 * guaranteed to be the user's last input, only the value change that caused this
 * apply; consecutive occurrences in one view/session show apply churn.
 */
export const panelBankSearchAppliedDuration = definePerfDuration({
  durationName: 'panel_bank_search_applied',
  startMark: panelBankSearchMarks.appliedInputStarted,
  endMark: panelBankSearchMarks.applied,
  description:
    'panel search latest input value to matching PanelBankSections search applied',
  owner: 'workspace',
  maxPairingDurationMs: PANEL_BANK_SEARCH_MAX_PAIRING_DURATION_MS,
  devtools: {
    track: 'Panel search',
    trackGroup: 'Workspace',
    color: 'secondary',
  },
});
